/**
 * OrderCashButton (formerly OrderDiscountButton)
 * -----------------------------------------------
 * Per owner request 2026-02: the inline pill on every order row is now
 * labelled **Cash** instead of Discount. Tapping it opens a dialog with
 * three one-tap options:
 *
 *   1. Cash · No Discount  — settle as plain cash, no discount applied.
 *   2. Cash 10% Off        — apply 10% discount, then settle as cash.
 *   3. Friends 20% Off     — apply 20% discount, then settle as cash.
 *
 * Plus a "Remove discount" footer when one is already on the order, and
 * the same Cash-only path lets the user clear an existing discount before
 * settling.
 *
 * Backend calls (in order):
 *   - POST /orders/{id}/discount  (only when changing the discount kind)
 *   - POST /payments/cash-received/{id}  (always — closes the sale)
 *
 * Manager/director only.
 */
import React, { useState } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Banknote, Sparkles, RotateCcw, Check } from 'lucide-react';
import { toast } from 'sonner';
import { openCashDrawer, isCashDrawerSupported } from '../lib/cashDrawer';

const DISCOUNT_OPTIONS = [
  { kind: 'cash_10',    label: 'Cash 10% Off',    percent: 10, accent: 'emerald' },
  { kind: 'friends_20', label: 'Friends 20% Off', percent: 20, accent: 'pink' },
];

export default function OrderDiscountButton({ order, onApplied }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const isPaid = ['paid', 'refunded'].includes(order.payment_status);
  const subtotal = Number(order.subtotal ?? order.total ?? 0);
  const activeKind = order.discount_kind || null;

  if (isPaid) return null;

  /**
   * Settle the order as cash, optionally applying / removing a discount
   * first. `discountKind`: 'cash_10' | 'friends_20' | 'none' | null
   *   - 'none' explicitly clears any existing discount before settling.
   *   - null means "leave the discount as-is, just settle".
   */
  const settleAsCash = async (discountKind) => {
    const key = `cash:${discountKind || 'asis'}`;
    setBusy(key);
    try {
      // Adjust the discount only when the caller explicitly asked for one
      // (or asked to remove it). 'asis' (null) skips the discount call so
      // we don't churn the audit log on every settle.
      if (discountKind && discountKind !== activeKind) {
        await api.post(`/orders/${order.id}/discount`, { kind: discountKind });
      }
      await api.post(`/payments/cash-received/${order.id}`);
      // Pop the till — best-effort, never blocks the settle. Errors get
      // a softer toast so staff know to check the WebUSB pairing.
      if (isCashDrawerSupported()) {
        try { await openCashDrawer(); }
        catch (drawerErr) { toast.error(`Drawer didn't open: ${drawerErr.message}`); }
      }
      const friendly = discountKind && discountKind !== 'none'
        ? `${DISCOUNT_OPTIONS.find(o => o.kind === discountKind).label} · paid in cash`
        : 'Cash payment confirmed';
      toast.success(friendly);
      setOpen(false);
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not settle');
    } finally {
      setBusy(null);
    }
  };

  /** Apply or clear a discount WITHOUT settling. Footer "Remove" path. */
  const applyDiscountOnly = async (discountKind) => {
    const key = `disc-only:${discountKind}`;
    setBusy(key);
    try {
      await api.post(`/orders/${order.id}/discount`, { kind: discountKind });
      toast.success(discountKind === 'none' ? 'Discount removed' : `${DISCOUNT_OPTIONS.find(o => o.kind === discountKind).label} applied`);
      setOpen(false);
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not apply discount');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className={`h-7 rounded-sm font-sans text-xs px-2 border ${
            activeKind
              ? 'bg-pink-500/15 text-pink-300 border-pink-500/40 hover:bg-pink-500/25'
              : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20'
          }`}
          data-testid={`discount-trigger-${order.id}`}
        >
          <Banknote className="w-3 h-3 mr-1" /> {activeKind ? `Cash · −${Math.round((order.discount_amount / Math.max(subtotal, 0.01)) * 100)}%` : 'Cash'}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-bg-secondary border border-emerald-500/30 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <Banknote className="w-4 h-4 text-emerald-400" /> Take cash payment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="rounded-sm border border-white/10 bg-white/[0.02] p-2 text-xs font-sans text-zinc-400 flex items-center justify-between">
            <span>Order #{order.id.slice(0, 8).toUpperCase()}</span>
            <span className="font-mono text-zinc-200">Subtotal ${subtotal.toFixed(2)}</span>
          </div>

          <div className="space-y-2">
            {/* Plain cash, no discount */}
            <button
              type="button"
              onClick={() => settleAsCash(activeKind ? null : 'none')}
              disabled={busy === 'cash:asis' || busy === 'cash:none'}
              className="w-full text-left rounded-sm border bg-emerald-500/15 text-emerald-200 border-emerald-500/40 hover:bg-emerald-500/25 transition-colors flex items-center justify-between px-3 py-2.5"
              data-testid={`cash-no-discount-${order.id}`}
            >
              <div>
                <p className="font-sans text-sm font-bold">Cash · No Discount</p>
                <p className="font-mono text-[11px] opacity-80">
                  {activeKind ? 'Settle for current total' : `Settle for $${subtotal.toFixed(2)}`}
                </p>
              </div>
              <Check className="w-4 h-4" />
            </button>
            {/* Discount + cash combos */}
            {DISCOUNT_OPTIONS.map((opt) => {
              const active = activeKind === opt.kind;
              const newTotal = subtotal * (1 - opt.percent / 100);
              const accent = opt.accent === 'emerald'
                ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25'
                : 'bg-pink-500/15 text-pink-300 border-pink-500/40 hover:bg-pink-500/25';
              return (
                <button
                  key={opt.kind}
                  type="button"
                  onClick={() => settleAsCash(opt.kind)}
                  disabled={busy === `cash:${opt.kind}`}
                  className={`w-full text-left rounded-sm border ${accent} transition-colors flex items-center justify-between px-3 py-2.5 ${
                    active ? 'ring-1 ring-current' : ''
                  }`}
                  data-testid={`discount-${opt.kind}-${order.id}`}
                >
                  <div>
                    <p className="font-sans text-sm font-bold">{opt.label}</p>
                    <p className="font-mono text-[11px] opacity-80">−${(subtotal * opt.percent / 100).toFixed(2)} → Pay ${newTotal.toFixed(2)} cash</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {active && <span className="text-[10px] font-sans uppercase tracking-wider">Active</span>}
                    <Sparkles className="w-3.5 h-3.5 opacity-70" />
                  </div>
                </button>
              );
            })}
          </div>
          {activeKind && (
            <Button
              variant="ghost"
              onClick={() => applyDiscountOnly('none')}
              disabled={busy === 'disc-only:none'}
              className="w-full h-9 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm font-sans text-xs"
              data-testid={`discount-remove-${order.id}`}
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Remove discount (don't settle yet)
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
